import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../bridge/client';
import { userDisplayName } from '../core/channelListDerivation';
import { dndDurations, dndEndTime } from '../core/accountSettings';
import { CustomStatusCodec } from '../core/customStatus';
import type { UserRecord } from '../core/records';
import { Icon } from '../design/icons';
import { t } from '../i18n/i18n';
import { Avatar } from './Avatar';
import { EmojiGlyph } from './features/emoji/EmojiGlyph';
import { requestIntent } from './features/settings/intent';
import { DndCustomDialog, dndUntilText } from './features/settings/DndCustom';
import { useLive } from './hooks';
import { statusLabel } from './features/settings/StatusSection';
import type { SettingsSection } from './features/settings/SettingsView';
import { ShellContext } from './shell/ShellContext';
import './features/settings/settings.css';

const STATUSES = ['online', 'away', 'dnd', 'offline'] as const;

/**
 * The own-profile popover at the sidebar's foot (the Mac's profile footer, plus the webapp's status
 * menu): who you are, your custom status, the four presences one click away (with how long "No
 * molestar" lasts), the doors to Mi perfil and Configuración, and sign out last.
 */
export function ProfileMenu({
  anchor,
  me,
  presence,
  onClose,
  onSignOut,
  onOpenSettings,
}: {
  anchor: DOMRect;
  me: UserRecord;
  presence: string | null;
  onClose(): void;
  onSignOut(): void;
  onOpenSettings(section: SettingsSection | null): void;
}) {
  const shell = useContext(ShellContext);
  const [dndOpen, setDndOpen] = useState(false);
  const [dndCustom, setDndCustom] = useState(false);
  const status = presence ?? 'offline';
  // §41.7: «No molestar · hasta 14:30» while DND has an end.
  const { value: dndEnd } = useLive(() => data.call('accountDndEnd'), ['syncState', 'userStatuses'], []);
  const dndUntil = status === 'dnd' ? dndUntilText(dndEnd ?? 0, Date.now()) : null;
  const now = Date.now();
  const cs = CustomStatusCodec.fromPropsJSON(me.customStatusJSON);
  const custom = cs && CustomStatusCodec.isActive(cs, now) ? cs : null;

  useEffect(() => {
    const close = (e: MouseEvent) => {
      // The «Personalizado…» dialog lives outside the popover (a scrim over the page).
      if (!(e.target as HTMLElement).closest('.profile-popover, .scrim')) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const id = setTimeout(() => window.addEventListener('mousedown', close));
    window.addEventListener('keydown', esc);
    return () => {
      clearTimeout(id);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  const setStatus = (value: string, end = 0) => {
    onClose();
    // Optimistic in the worker: the footer dot changes on the click; a refusal restores it.
    data.call('accountSetStatus', value, end).then(
      (ok) => !ok && shell?.toast(t('profile.error.statusUpdate'), 'error'),
      () => shell?.toast(t('profile.error.statusUpdate'), 'error'),
    );
  };

  const open = (section: SettingsSection | null) => {
    onClose();
    onOpenSettings(section);
  };

  return (
    <div
      className="popover profile-popover"
      // Desktop opens it from the sidebar's FOOT (above the anchor); the phone from the top bar's avatar
      // (below it). Either way it stays on screen (it used to open off the top-right at phone width).
      style={{
        position: 'fixed',
        left: Math.max(8, Math.min(anchor.left, window.innerWidth - 280 - 8)),
        width: 280,
        maxHeight: 'calc(100vh - 16px)',
        overflowY: 'auto',
        ...(anchor.top > window.innerHeight / 2 ? { bottom: window.innerHeight - anchor.top + 8 } : { top: anchor.bottom + 8 }),
      }}
      // A panel of mixed controls (status, custom status, links), so a dialog, not a menu (P255).
      role="dialog"
      aria-label={userDisplayName(me)}
    >
      <div className="pm-head">
        <span aria-hidden>
          <Avatar userId={me.id} name={userDisplayName(me)} size={40} version={me.lastPictureUpdate} presence={status} />
        </span>
        <div className="pm-head-text">
          <span className="pm-name">{userDisplayName(me)}</span>
          <span className="pm-username">@{me.username}</span>
        </div>
      </div>
      <div className="pm-custom-row">
        <button
          type="button"
          className="pm-custom"
          onClick={() => {
            requestIntent('customStatus');
            open('status');
          }}
        >
          <span className="st-emoji">{custom ? <EmojiGlyph name={custom.emoji} /> : <Icon name="smilePlus" size={17} />}</span>
          <span className={`pm-custom-text${custom ? '' : ' muted'}`}>{custom ? custom.text : t('profile.customStatus.set')}</span>
        </button>
        {custom ? (
          // A sibling of the status button, never inside it (nested controls, P255).
          <button
            type="button"
            className="pm-custom-clear"
            title={t('profile.customStatus.clear')}
            aria-label={t('profile.customStatus.clear')}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              data.call('accountClearCustomStatus').then((ok) => !ok && shell?.toast(t('profile.customStatus.failed'), 'error'));
            }}
          >
            <Icon name="x" size={13} />
          </button>
        ) : null}
      </div>
      <div className="menu-sep" />
      {STATUSES.map((value) => (
        <div key={value}>
          <button
            type="button"
            className="menu-item"
            aria-pressed={status === value}
            aria-expanded={value === 'dnd' ? dndOpen : undefined}
            onClick={() => (value === 'dnd' ? setDndOpen((o) => !o) : setStatus(value))}
          >
            <span className={`presence st-dot ${value}`} style={{ width: 10, height: 10 }} aria-hidden />
            {value === 'dnd' && dndUntil ? (
              <span className="pm-dnd-label">
                {statusLabel(value)}
                <span className="pm-dnd-until">{dndUntil}</span>
              </span>
            ) : (
              statusLabel(value)
            )}
            {value === 'dnd' ? <Icon name={dndOpen ? 'chevronUp' : 'chevronDown'} size={14} style={{ marginLeft: 'auto' }} /> : status === value ? <Icon name="check" size={14} style={{ marginLeft: 'auto' }} /> : null}
          </button>
          {value === 'dnd' && dndOpen
            ? dndDurations.map((d) => (
                <button key={d} type="button" className="menu-item" style={{ paddingLeft: 34 }} onClick={() => setStatus('dnd', dndEndTime(d, Date.now()))}>
                  {t(`web.settings.dnd.${d}`)}
                </button>
              ))
            : null}
          {value === 'dnd' && dndOpen ? (
            <button type="button" className="menu-item" style={{ paddingLeft: 34 }} onClick={() => setDndCustom(true)}>
              {t('web.dnd.custom')}
            </button>
          ) : null}
        </div>
      ))}
      {status === 'ooo' ? (
        <button type="button" className="menu-item" aria-pressed onClick={() => open('status')}>
          <span className="presence st-dot ooo" style={{ width: 10, height: 10 }} aria-hidden />
          {statusLabel('ooo')}
          <Icon name="check" size={14} style={{ marginLeft: 'auto' }} />
        </button>
      ) : null}
      <div className="menu-sep" />
      <button type="button" className="menu-item" onClick={() => open('profile')}>
        <Icon name="person" size={16} />
        {t('web.settings.myProfile')}
      </button>
      <button type="button" className="menu-item" onClick={() => open(window.matchMedia('(max-width: 760px)').matches ? null : 'notifications')}>
        <Icon name="gear" size={16} />
        {t('web.settings.openSettings')}
      </button>
      <div className="menu-sep" />
      {dndCustom
        ? createPortal(
            <DndCustomDialog
              onClose={() => setDndCustom(false)}
              onPick={(end) => {
                setDndCustom(false);
                setStatus('dnd', end);
              }}
            />,
            document.body,
          )
        : null}
      <button type="button" className="menu-item danger" onClick={onSignOut}>
        <Icon name="signOut" size={16} />
        {t('profile.signOut')}
      </button>
    </div>
  );
}
